/**
 * Something on screen while a page is being fetched.
 *
 * Alan, 2026-08-09: "when its full white screen and loading please add circle
 * motion or words you choose to sign its loading."
 *
 * The boot ring in index.html is hidden the moment app.js runs, and every route
 * after that awaits its data before it calls shell() - so the Dashboard, which
 * pulls all five pages of Containers before the first paint, left the window
 * blank for the whole wait with nothing to say it was working.
 *
 * This lives on <body> rather than inside #app on purpose: shell() replaces the
 * whole of #app, so an indicator parked in there is destroyed by the very render
 * it is waiting for.
 */
const OVERLAY_ID = 'routeLoadingOverlay';

/**
 * A route that never settles must not leave a curtain over the whole app.
 *
 * The same rule the busy button already follows (BUSY_RELEASE_MS): the
 * indicator is a promise that something is happening, not a lock, so it lets go
 * on its own if the thing never finishes. This mattered from 2026-08-11, when
 * the overlay started actually being visible - before that the ring was hidden
 * in the same tick it appeared, so a hung route stranded nothing.
 */
export const ROUTE_LOADING_RELEASE_MS = 20_000;
let releaseTimer = null;

function clearRelease(view) {
  if (releaseTimer === null) return;
  view?.clearTimeout?.(releaseTimer);
  releaseTimer = null;
}

export function routeLoadingMarkup(label = '载入中') {
  return `<div class="route-loading__panel" role="status" aria-live="polite">
      <span class="boot-ring" aria-hidden="true"></span>
      <span class="route-loading__label">${label}</span>
    </div>`;
}

export function showRouteLoading(documentRef = globalThis.document, label = '载入中') {
  const body = documentRef?.body;
  if (!body) return null;
  let overlay = documentRef.getElementById(OVERLAY_ID);
  if (!overlay) {
    overlay = documentRef.createElement('div');
    overlay.id = OVERLAY_ID;
    overlay.className = 'route-loading';
    overlay.innerHTML = routeLoadingMarkup(label);
    body.appendChild(overlay);
  }
  // The label is set on every show, not only when the overlay is created. It is
  // reused across routes and, since 2026-08-19, across fleet loads - so an overlay
  // built once for "载入中" would have gone on saying that while the Containers
  // were being read.
  const label_ = overlay.querySelector?.('.route-loading__label');
  if (label_) label_.textContent = label;
  overlay.hidden = false;
  const view = documentRef.defaultView ?? globalThis;
  clearRelease(view);
  releaseTimer = view?.setTimeout?.(() => {
    releaseTimer = null;
    overlay.hidden = true;
  }, ROUTE_LOADING_RELEASE_MS) ?? null;
  return overlay;
}

export function hideRouteLoading(documentRef = globalThis.document) {
  const overlay = documentRef?.getElementById?.(OVERLAY_ID);
  clearRelease(documentRef?.defaultView ?? globalThis);
  if (overlay) overlay.hidden = true;
  return overlay;
}
